import { ReactComponent as ArrowSVG } from '@assets/SVGs/PresenteeismTable/ArrowSVG.svg';
import { ReactComponent as SearchSVG } from '@assets/SVGs/PresenteeismTable/SearchSVG.svg';
import Table from '@components/General/Table';
import { TableCell } from '@mui/material';
import styles from '@styles/components/Novedades/NovedadesReporte.module.css';
import { useEffect, useMemo, useState } from 'react';
import { QontyEmptyState } from '@components/QontyEmptyState';
import { QontyLoading } from '@components/QontyLoading';

import { usePopup } from '@contexts/popupContext';
import EditarNovedad from './EditarNovedad';
import {Application} from '@core/Application';

// import '@styles/Debugging.css'
import Image from '@components/General/Image';
import ResultPopup from '@components/General/ResultPopup';
import { NovedadPickerPopper } from '@components/General/TablePoppers';
import { APIIntegration, apiConfig } from '@integrations/APIIntegration';
import * as XLSX from 'xlsx';
import Utils from '@utils/Utils';
import NovedadesReferencias from './NovedadesReferencias';
import { getNovedadColors } from '@utils/NovedadDefaultColors';
import { GhostButton, HollowedButton, RefreshButton } from '@shared/designSystem/Button';
import { HorizontalContainer } from '@shared/designSystem/Containers';
import designSystemStyles from '@shared/styles/designSystemStyles';
import SearchBar from '@shared/designSystem/SearchBar';


const hexColorPattern = /^#(?:[0-9a-fA-F]{3}){1,2}$/;

const isValidHexColor = (color) => typeof color === 'string' && hexColorPattern.test(color.trim());

const getConfiguredNovedad = (novedad, novedadesByAcronym) => {
    if (!novedad?.acronym) {
        return novedad;
    }

    const configuredNovedad = novedadesByAcronym.get(novedad.acronym);
    if (!configuredNovedad) {
        return novedad;
    }

    return {
        ...novedad,
        backgroundColor: configuredNovedad.backgroundColor ?? novedad.backgroundColor,
        foregroundColor: configuredNovedad.foregroundColor ?? novedad.foregroundColor,
    };
};

const getNovedadCellStyle = (novedad) => {
    if (!novedad) {
        return {};
    }

    const colors = getNovedadColors(novedad);
    const backgroundColor = isValidHexColor(colors?.backgroundColor) ? colors.backgroundColor.trim() : undefined;
    const foregroundColor = isValidHexColor(colors?.foregroundColor) ? colors.foregroundColor.trim() : undefined;

    if (!backgroundColor && !foregroundColor) {
        return {};
    }

    return {
        backgroundColor,
        color: foregroundColor,
        border: backgroundColor ? `1.5px solid ${backgroundColor}` : undefined,
        fontWeight: 700,
    };
};

const NovedadCell = ({row, date, dayIndex, novedadData, novedades, novedadesByAcronym, handleNovedadChangeForCell, handleNovedadChangeForCells, timezone}) => {
    const {showPopup} = usePopup()
    const [novedad, setNovedad] = useState(novedadData)
    const displayNovedad = getConfiguredNovedad(novedad, novedadesByAcronym)

    const emptyStyle = {
        position: 'relative',
        border: '1.5px solid #FFCA40',
        borderRadius: '2.5px',
        cursor: 'pointer'
    };

    const triangleStyle = {
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: 0,
        height: 0,
        borderLeft: '15px solid transparent',
        borderBottom: '15px solid #FFCA40',
    };


    const changeNovedadForCurrentCell = async (nuevaNovedad) => {
        try {

            const data = {
                novedad: nuevaNovedad.name,
                novedadAcronym: nuevaNovedad.acronym,
                userID: row.id
            };

            const novedadesService = Application.shared().coreDependencies.novedadesService
            await novedadesService.changeUserStatusForDay(row.orgID, date, data)

            showPopup(() => <ResultPopup result={"Novedad cambiada correctamente!"} succesful={true}/>)
            setNovedad(nuevaNovedad)
            handleNovedadChangeForCell(row.id, dayIndex, nuevaNovedad)
        } catch (error) {
            showPopup(() => <ResultPopup result={"Error al guardar novedad"} succesful={false}/>)
            console.error(error)
        }
    }

    const changeNovedadForFechas = async (nuevaNovedad, fechas) => {
        try {
            let formattedDates = []
            for (let fecha of fechas) {
                let formattedDate = Utils.getDayStringFromTimestamp(fecha.getTime(), timezone)
                formattedDates.push(formattedDate)
            }

            const data = {
                novedad: nuevaNovedad.name,
                novedadAcronym: nuevaNovedad.acronym,
                userID: row.id,
                dates: formattedDates
            };

            const novedadesService = Application.shared().coreDependencies.novedadesService
            await novedadesService.changeUserStatusInBulk(row.orgID, data)

            showPopup(() => <ResultPopup result={"Novedad cambiada correctamente!"} succesful={true}/>)
            handleNovedadChangeForCells()
        } catch (error) {
            showPopup(() => <ResultPopup result={"Error al guardar novedad"} succesful={false}/>)
            console.error(error)
        }
    }

    const handleChangeNovedad = async (nuevaNovedad, fechas, isSameDay = false) => {
        // if (nuevaNovedad === novedad) return
        if (nuevaNovedad === null) {
            showPopup(() => <ResultPopup result={"Elige una novedad!"} succesful={false}/>)
            return
        } 

        if (isSameDay) {
            changeNovedadForCurrentCell(nuevaNovedad)
        } else {
            changeNovedadForFechas(nuevaNovedad, fechas)
        }

    }

      
    const handleClick = (e) => {
        e.stopPropagation()
        
        showPopup(() => 
        <EditarNovedad 
            colaboradorName={row.colaborador} 
            colaboradorPhoto={row.photo} 
            novedad={novedad} 
            novedades={novedades}
            onAccept={handleChangeNovedad}
            defaultDate={date}
        />, false)
    };


    useEffect(() => {
        setNovedad(novedadData)
    }, [novedadData])


    return (
        <TableCell className={styles['cell-style']} style={!displayNovedad ? emptyStyle : getNovedadCellStyle(displayNovedad)} align="center" padding='none' onClick={handleClick}>
            {displayNovedad ?
                displayNovedad.acronym
            :
                <div style={triangleStyle}/>
            }
        </TableCell>
    )

}


const TableRow = ({row, columns, novedades, novedadesByAcronym, handleNovedadChangeForCell, handleNovedadChangeForCells, timezone, shouldHaveLegajoColumn}) => {

    const processName = (name) => {
        const words = name.split(' ');
        const processedWords = words.map(word => {
            const firstLetter = word.charAt(0).toUpperCase();
            const restOfWord = word.slice(1).toLowerCase();
            return firstLetter + restOfWord;
        });
        return processedWords.join(' ');        
    }
    
    const dayColumns = columns.filter(column => column.dataKey.startsWith('day'));

    return (
        <>
        <TableCell className={`${styles['cell-style']} ${styles['sticky-colaborador-cell']}`} style={{paddingLeft: '15px'}} padding='none'>
            <div style={{display: 'flex', width: '100%', height: '100%', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', flexGrow: 0, flexShrink: 0}}>
                <div className={styles['circle-mask']}  style={{width: '24px', height: '24px', marginRight: '10px', display: 'inline-block', verticalAlign: 'top'}}>
                    <Image src={row.photo || '/assets/Q.png'} alt="Profile Picture" isRound={true} style={{width: '100%', height: '100%'}}/>
                </div>
                <div style={{
                    display: 'inline-block', 
                    maxWidth: 'calc(100% - 34px)',
                    fontSize: '12px',
                    color: '#101828', 
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis'
                }}>  
                    {processName(row.colaborador)}
                </div>
            </div>
        </TableCell>
        {shouldHaveLegajoColumn &&
            <TableCell className={styles['cell-style']} style={{paddingLeft: '15px', width: '90px', minWidth: '90px', maxWidth: '90px'}} padding='none'>
                <div style={{display: 'flex', width: '100%', height: '100%', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', flexGrow: 0, flexShrink: 0}}>
                    <p style={{fontSize: '12px', color: row.legajo ? '#101828' : 'lightgray', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                        {row.legajo ? row.legajo : 'N/A'}
                    </p>
                </div>
            </TableCell>
        }
        {dayColumns.map((dayColumn, index) => {
            let dayIndex = parseInt(dayColumn.label)
            let novedad = row.novedades[dayIndex]
            let date = `${row.yearMonth}-${dayIndex.toString().padStart(2, '0')}`
            return (
                <NovedadCell key={`${row.id}-${dayIndex}`} date={date} dayIndex={dayIndex} row={row} novedadData={novedad} novedades={novedades} novedadesByAcronym={novedadesByAcronym} handleNovedadChangeForCell={handleNovedadChangeForCell} handleNovedadChangeForCells={handleNovedadChangeForCells} timezone={timezone}/>
            )
        })}
        </>
    )
}


const months = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
]; // TODO: localize this


export default function NovedadesReporte ({orgID, timezone}) {
    const { showPopup } = usePopup()

    const [novedades, setNovedades] = useState([])
    const novedadesByAcronym = useMemo(() => {
        return new Map(
            novedades
                .filter(novedad => novedad?.acronym)
                .map(novedad => [novedad.acronym, novedad])
        );
    }, [novedades])

    const [loadingReporte, setLoadingReporte] = useState(true);
    const [loadingNovedades, setLoadingNovedades] = useState(true);
    const [response, setResponse] = useState([]);

    const [searchField, setSearchField] = useState('');

    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // Default to current month
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())

    const [filter, setFilter] = useState({acronym: '', name: 'Todos'})
    
    const [openFilter, setOpenFilter] = useState(false)
    const [anchorEl, setAnchorEl] = useState(null)

    const [daysInMonth, setDaysInMonth] = useState(new Date(selectedYear, selectedMonth, 0).getDate())
    const shouldHaveLegajoColumn = response.some(row => row.legajo)

    const fetchReporteList = async () => {
        const yearMonth = `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}`

        const transformData = (data) => {
            let result = []
            data.forEach((row) => {
                if (row.deactivationTimestamp && Object.keys(row.novedades).length === 0) return

                result.push({
                    id: row.id,
                    photo: row.imageURL,
                    colaborador: row.name,
                    legajo: row.legajo ? row.legajo : undefined,
                    yearMonth: yearMonth,
                    novedades: row.novedades,
                    orgID: orgID
                })
            })
            return result
        }

        setLoadingReporte(true)
        try {

            const novedadesService = Application.shared().coreDependencies.novedadesService
            let response = await novedadesService.getReporteNovedades(orgID, yearMonth)

            setResponse(transformData(response))
        } catch (error) {
            console.error(error)
        } finally {
            setLoadingReporte(false)
        }
    }

    const fetchNovedades = async () => {
        setLoadingNovedades(true)
        try {
            const novedadesService = Application.shared().coreDependencies.novedadesService
            let response = await novedadesService.getNovedades(orgID)
            setNovedades(response)
        } catch (error) {
            console.error(error)
        } finally {
            setLoadingNovedades(false)
        }
    }

    useEffect(() => { //Fetch novedades list when orgID changes
        fetchNovedades()
    }, [orgID])

    useEffect(() => { //Fetch reporte list when orgID changes, set days in month if selectedMonth changes
        fetchReporteList()
        setDaysInMonth(new Date(selectedYear, selectedMonth, 0).getDate())
    }, [orgID, selectedMonth, selectedYear])

    const columns = useMemo(() => {
        const styles = {
            color: '#667085',
            textAlign: 'center',
            fontFamily: 'Inter',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: '18px'
        }

        const columns = [
            {  
                label: 'Colaborador',  
                dataKey: 'colaborador',  
                size: '260px',
                style: {
                    ...styles,
                    minWidth: '260px',
                    maxWidth: '260px',
                    position: 'sticky',
                    left: 0,
                    zIndex: 8,
                    backgroundColor: '#F6F7F9',
                    boxShadow: '2px 0 0 #EAECF0',
                },
            }
        ];

        if (shouldHaveLegajoColumn) {
            columns.push({         
                label: 'Legajo',
                dataKey: 'legajo',
                size: '90px',
                style: {
                    ...styles,
                    minWidth: '90px',
                    maxWidth: '90px',
                },
            })
        }

        for (let i = 1; i <= daysInMonth; i++) {
            columns.push({
                label: `${i}`,
                dataKey: `day${i}`,
                size: '72px',
                style: {
                    ...styles,
                    minWidth: '72px',
                    maxWidth: '72px',
                }
            });
        }
        return columns;
    }, [daysInMonth, shouldHaveLegajoColumn]);



    const handleClickReferencias = () => {
        showPopup(() => <NovedadesReferencias novedades={novedades}/>)
    }

    const handleClickFilter = (event) => {
        setAnchorEl(event.currentTarget);
        setOpenFilter((previousOpen) => !previousOpen);
    };

    const handleFilterChange = (filter) => {
        if (filter) {
            setFilter(filter)
        } else {
            setFilter({acronym: '', name: 'Todos'})
        }
    }


    const incrementMonth = () => {
        let newMonth;
        if (selectedMonth === 12) {
            setSelectedYear(prevYear => prevYear + 1);
            newMonth = 1;
        } else {
            newMonth = selectedMonth + 1;
        }
        handleMonthChange(newMonth);
    };

    const decrementMonth = () => {
        let newMonth;
        if (selectedMonth === 1) {
            setSelectedYear(prevYear => prevYear - 1);
            newMonth = 12;
        } else {
            newMonth = selectedMonth - 1;
        }
        handleMonthChange(newMonth);
    };

    const handleMonthChange = (newMonth) => {
        setSelectedMonth(newMonth);
    };



    const handleNovedadChangeForCell = (userID, dayIndex, novedad) => {
        setResponse(previousResponse => previousResponse.map(userRow => {
            if (userRow.id !== userID) {
                return userRow;
            }

            return {
                ...userRow,
                novedades: {
                    ...userRow.novedades,
                    [dayIndex]: novedad,
                },
            };
        }))
    }

    const handleNovedadChangeForCells = () => {
        fetchReporteList()
    }

    const filteredRows = response
        .filter(userRow => {
            return userRow.colaborador.toLowerCase().replace(/\s/g, '').includes(searchField.toLowerCase().replace(/\s/g, ''))
        })
        .filter(userRow => {
            if (filter.name === 'Todos') {
                return true;
            }
            if (filter.name === 'Pendientes') {
                for (let i = 1; i <= daysInMonth; i++) {
                    if (!userRow.novedades[i]) {
                        return true;
                    }
                }
                return false;
            }

            return Object.values(userRow.novedades).some(novedad => novedad.name === filter.name);
        })

    const loading = loadingReporte || loadingNovedades

    const loadingView = (
        <QontyLoading
            text={"Cargando reporte de novedades...".localized()}
            withQonty={true}
        />
    )

    const emptyView = (
        <QontyEmptyState
            text={"No hay información para esta búsqueda.".localized()}
            darkMode={false}
        />
    )

    const reportTableMinWidth = 260 + (shouldHaveLegajoColumn ? 90 : 0) + (daysInMonth * 72)

    function rowTemplate(_index, row, columns) {
        return (
            <TableRow 
                row={row}
                shouldHaveLegajoColumn={shouldHaveLegajoColumn}
                columns={columns} 
                novedades={novedades} 
                novedadesByAcronym={novedadesByAcronym}
                handleNovedadChangeForCell={handleNovedadChangeForCell} 
                handleNovedadChangeForCells={handleNovedadChangeForCells} 
                timezone={timezone}
            />
        );
    }

    const generateXLS = () => {
        const fields = ['Nombre colaborador'];
        if (columns.some(column => column.dataKey === 'legajo')) {
            fields.push('Legajo');
        }
        fields.push(...Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString()));

        const xlsData = filteredRows.map(item => {
            const row = { 'Nombre colaborador': item.colaborador };
            if (columns.some(column => column.dataKey === 'legajo')) {
                row['Legajo'] = item.legajo || 'N/A';
            }
            for (let i = 1; i <= daysInMonth; i++) {
                row[i] = item.novedades[i] ? item.novedades[i].acronym : '';
            }
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(xlsData, { header: fields });
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

        const xlsBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([xlsBuffer], { type: 'application/octet-stream' });

        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;
        const yearMonth = `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}`;
        link.setAttribute('download', `${yearMonth}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <>
        <div className={styles['nomina-header']}>
            <HorizontalContainer>
                <SearchBar
                    placeholder={"Buscar colaborador".localized()}
                    value={searchField}
                    onChange={setSearchField}
                    width="350px"
                />
                <RefreshButton
                    onClick={ () => fetchReporteList()}
                />
            </HorizontalContainer>

            <HorizontalContainer
                justifyContent="flex-end"
                gap={designSystemStyles.gapLg}
            >
                <GhostButton
                    onClick={handleClickReferencias}
                    text={"Referencias".localized()}
                    width="180px"
                />

                <div className={styles["filtrar-container"]}>
                    Filtrar
                    <div className={styles["filtrar-box"]} onClick={handleClickFilter}>

                        {filter.name}

                        <div className={styles["svg-container"]} style={{transform: openFilter ? 'none' : 'rotate(180deg)', transition: 'transform 0.2s ease'}}>
                            <ArrowSVG/>
                        </div>
                        {openFilter && <NovedadPickerPopper 
                            isOpen={openFilter} 
                            anchorElement={anchorEl} 
                            placement='bottom' 
                            clickAwayHandler={() => setOpenFilter(false)}
                            novedades={[{acronym: '', name: 'Todos'}, {acronym: '', name: 'Pendientes'}, ...novedades]}
                            selectedNovedad={filter}
                            setSelectedNovedad={handleFilterChange}
                        />}
                    </div>
                </div>
                <div className={styles["month-changer-container"]}>
                    {months[selectedMonth - 1]} {selectedYear}
                    <div className={styles["month-buttons"]}>
                        <div className={styles["arrow-svg-container"]} style={{transform: "rotate(90deg)"}} onClick={decrementMonth}>
                            <ArrowSVG/>
                        </div>

                        <div className={styles["arrow-svg-container"]} style={{transform: "rotate(-90deg)"}} onClick={incrementMonth}>
                            <ArrowSVG/>
                        </div>
                    </div>
                </div>
            </HorizontalContainer>



        </div> 

        <div className={styles['table-container']}>
            {loading
                ? loadingView
                : filteredRows.length === 0
                    ? emptyView
                    : (
                        <div
                            className={styles['report-table-scroll']}
                            style={{
                                '--novedades-report-table-width': `${reportTableMinWidth}px`,
                            }}
                        >
                            <Table
                                loading={false}
                                dataForRows={filteredRows}
                                columns={columns}
                                rowTemplate={(_index, row) => rowTemplate(_index, row, columns)}
                            />
                        </div>
                    )}
        </div>
        <HorizontalContainer justifyContent="flex-end" paddingTop={designSystemStyles.paddingMd} width="100%">
            <HollowedButton 
                onClick={() => {!loading && generateXLS()}}
                text={"Exportar reporte a Excel".localized()}
                width="250px"
            />
        </HorizontalContainer>

        {/* <div className={styles['pagination-controls']}>  
            <div className={styles['exportar-button']} onClick={() => {!loading && generateXLS()}}>
                    <div className={styles['svg-container']} style={{marginRight: '8px'}}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18" fill="none">
                            <path d="M15.9333 12.258C15.8883 12.1489 15.8251 12.0485 15.7466 11.961L13.0792 9.261C12.9117 9.09153 12.6847 8.99632 12.4479 8.99632C12.2111 8.99632 11.984 9.09153 11.8166 9.261C11.6492 9.43047 11.5551 9.66033 11.5551 9.9C11.5551 10.1397 11.6492 10.3695 11.8166 10.539L12.9725 11.7H8.00222C7.7664 11.7 7.54025 11.7948 7.3735 11.9636C7.20676 12.1324 7.11308 12.3613 7.11308 12.6C7.11308 12.8387 7.20676 13.0676 7.3735 13.2364C7.54025 13.4052 7.7664 13.5 8.00222 13.5H12.9725L11.8166 14.661C11.7333 14.7447 11.6671 14.8442 11.622 14.9539C11.5768 15.0636 11.5536 15.1812 11.5536 15.3C11.5536 15.4188 11.5768 15.5364 11.622 15.6461C11.6671 15.7558 11.7333 15.8553 11.8166 15.939C11.8993 16.0234 11.9976 16.0903 12.106 16.136C12.2143 16.1817 12.3305 16.2052 12.4479 16.2052C12.5653 16.2052 12.6815 16.1817 12.7898 16.136C12.8982 16.0903 12.9965 16.0234 13.0792 15.939L15.7466 13.239C15.8289 13.1545 15.8926 13.0533 15.9333 12.942C16.0222 12.7229 16.0222 12.4771 15.9333 12.258ZM9.78049 16.2H2.66741C2.43159 16.2 2.20544 16.1052 2.03869 15.9364C1.87195 15.7676 1.77827 15.5387 1.77827 15.3V2.7C1.77827 2.46131 1.87195 2.23239 2.03869 2.0636C2.20544 1.89482 2.43159 1.8 2.66741 1.8H7.11308V4.5C7.11308 5.21608 7.39411 5.90284 7.89435 6.40919C8.39458 6.91554 9.07305 7.2 9.78049 7.2H13.337C13.5126 7.19911 13.6839 7.14565 13.8295 7.04635C13.9751 6.94705 14.0883 6.80636 14.155 6.642C14.2231 6.4781 14.2417 6.29746 14.2085 6.12287C14.1753 5.94828 14.0917 5.78756 13.9683 5.661L8.6335 0.261C8.56 0.190995 8.47597 0.133273 8.38455 0.0899999H8.30452L8.05556 0H2.66741C1.95997 0 1.2815 0.284464 0.781265 0.790812C0.281029 1.29716 0 1.98392 0 2.7V15.3C0 16.0161 0.281029 16.7028 0.781265 17.2092C1.2815 17.7155 1.95997 18 2.66741 18H9.78049C10.0163 18 10.2425 17.9052 10.4092 17.7364C10.5759 17.5676 10.6696 17.3387 10.6696 17.1C10.6696 16.8613 10.5759 16.6324 10.4092 16.4636C10.2425 16.2948 10.0163 16.2 9.78049 16.2ZM8.89135 3.069L11.1942 5.4H9.78049C9.54467 5.4 9.31852 5.30518 9.15177 5.1364C8.98503 4.96761 8.89135 4.73869 8.89135 4.5V3.069Z" fill="white"/>
                        </svg>
                    </div>
                    <p>Exportar</p>
                </div>
        </div> */}
        </>
    )
}
